import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Compass, HeartHandshake, Sparkles, Globe2, Quote,
  PartyPopper, Target, Rocket, Star, GraduationCap, Users, School,
  Building2, Ticket, Sprout, Store, Dumbbell, Briefcase,
} from "lucide-react";
import Header from "../components/Header";
import RouteModal from "../components/RouteModal";
import AssistantWidget from "../components/AssistantWidget";
import { brand, statValues, programs, valueIcons, testimonialAvatars } from "../mock/mock";
import { useLang } from "../i18n/LanguageContext";

const VAL_ICONS = { Compass, HeartHandshake, Sparkles, Globe2 };
const PROG_ICONS = {
  PartyPopper, Target, Rocket, Star, GraduationCap, Users, School,
  Building2, Ticket, Sprout, Store, Dumbbell, Briefcase,
};

export default function Landing() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [modal, setModal] = useState(false);

  return (
    <div className="bg-cream">
      {/* HERO */}
      <section className="relative min-h-screen flex flex-col overflow-hidden">
        <img src={brand.heroImage} alt="Líderes SUPERA" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,28,48,0.72) 0%, rgba(10,28,48,0.82) 55%, rgba(10,28,48,0.95) 100%)" }} />
        <Header dark />

        <div className="relative z-10 flex-1 max-w-[1400px] w-full mx-auto px-6 md:px-10 flex flex-col justify-center pt-24">
          <p className="text-center text-xs md:text-sm font-extrabold tracking-[0.3em] text-gold animate-fade-up">
            {t.kicker}
          </p>
          <h1 className="mt-6 max-w-3xl text-5xl md:text-7xl font-extrabold text-white leading-[1.02] animate-fade-up delay-1">
            {t.heroPre} <span className="font-serif-it text-gold">{t.heroAccent}</span><br />
            {t.heroPost}
          </h1>
          <p className="mt-6 max-w-xl text-lg text-white/75 animate-fade-up delay-2">
            {t.heroSubtitle}
          </p>
        </div>

        <div className="relative z-10 max-w-[1400px] w-full mx-auto px-6 md:px-10 pb-8">
          <button
            onClick={() => setModal(true)}
            className="group w-full flex items-center justify-center gap-3 rounded-2xl bg-[var(--gold)] hover:bg-[var(--gold-soft)] text-[#0A1C30] font-extrabold text-lg py-5 shadow-[0_10px_40px_-8px_rgba(245,197,24,0.5)] transition-colors animate-fade-up delay-3"
          >
            {t.ctaStart}
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
          </button>
          <p className="text-center mt-5 text-white/80 text-sm animate-fade-up delay-3">
            {t.haveAccount}{" "}
            <button onClick={() => navigate("/login")} className="font-bold text-gold hover:underline underline-offset-4">
              {t.login}
            </button>
          </p>

          <div className="mt-8 border-t border-white/10 pt-6 flex items-center justify-center gap-10 md:gap-16 animate-fade-up delay-4">
            {statValues.map((v, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl md:text-3xl font-extrabold text-gold">{v}</div>
                <div className="text-xs md:text-sm text-white/60 tracking-wide">{t.statLabels[i]}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAMAS */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <p className="text-xs font-extrabold tracking-[0.25em] text-[#B99B2E]">{t.programsKicker}</p>
        <h2 className="mt-2 text-4xl md:text-5xl font-extrabold text-[#0A1C30] max-w-2xl">
          {t.programsTitlePre} <span className="font-serif-it text-[#E9A400]">{t.programsTitleAccent}</span> {t.programsTitlePost}
        </h2>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {programs.map((p) => {
            const Icon = PROG_ICONS[p.icon];
            return (
              <button
                key={p.id}
                onClick={() => navigate(`/registro/${p.id}`)}
                className="group text-left bg-white rounded-3xl overflow-hidden ring-1 ring-black/5 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
              >
                <div className="relative h-44 overflow-hidden">
                  <img src={p.img} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  <span className="absolute top-3 left-3 w-11 h-11 rounded-xl flex items-center justify-center shadow-md" style={{ background: p.bg }}>
                    {Icon && <Icon className="w-5 h-5" style={{ color: p.color }} />}
                  </span>
                  <span className="absolute top-4 right-4 text-[11px] font-extrabold tracking-wide uppercase text-[var(--gold)] drop-shadow-[0_1px_4px_rgba(0,0,0,0.85)]">{p.meta}</span>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-extrabold text-[#0A1C30]">{p.name}</h3>
                  <p className="text-sm text-[#6B7688] mt-0.5">{p.age}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#0A1C30] group-hover:text-[#E9A400] transition-colors">
                    {t.enroll} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* VALORES */}
      <section className="bg-navy py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="text-xs font-extrabold tracking-[0.25em] text-gold">{t.whyKicker}</p>
          <h2 className="mt-2 text-4xl md:text-5xl font-extrabold text-white max-w-2xl">
            {t.whyTitlePre} <span className="font-serif-it text-gold">{t.whyTitleAccent}</span>{t.whyTitlePost}
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.values.map((v, i) => {
              const Icon = VAL_ICONS[valueIcons[i]];
              return (
                <div key={i} className="rounded-3xl bg-white/5 ring-1 ring-white/10 p-7 hover:bg-white/10 transition-colors">
                  <span className="w-12 h-12 rounded-xl bg-[var(--gold)] flex items-center justify-center">
                    {Icon && <Icon className="w-6 h-6 text-[#0A1C30]" />}
                  </span>
                  <h3 className="mt-5 text-lg font-extrabold text-white">{v.title}</h3>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* METODO */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <p className="text-xs font-extrabold tracking-[0.25em] text-[#B99B2E]">{t.methodKicker}</p>
        <h2 className="mt-2 text-4xl md:text-5xl font-extrabold text-[#0A1C30] max-w-3xl">{t.methodTitle}</h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.method.map((m, i) => (
            <div key={i} className="relative rounded-3xl bg-white p-7 ring-1 ring-black/5">
              <span className="text-5xl font-extrabold text-[#EFE7C9]">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-3 text-lg font-extrabold text-[#0A1C30]">{m.title}</h3>
              <p className="mt-2 text-sm text-[#6B7688] leading-relaxed">{m.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIOS */}
      <section className="bg-cream py-8 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <h2 className="text-4xl md:text-5xl font-extrabold text-[#0A1C30] max-w-2xl">
            {t.storiesPre} <span className="font-serif-it text-[#E9A400]">{t.storiesAccent}</span>{t.storiesPost}
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.testimonials.map((tm, i) => (
              <div key={i} className="rounded-3xl bg-white p-7 ring-1 ring-black/5">
                <Quote className="w-8 h-8 text-[var(--gold)]" />
                <p className="mt-4 text-[#2A3446] leading-relaxed">“{tm.text}”</p>
                <div className="mt-6 flex items-center gap-3">
                  <img src={testimonialAvatars[i]} alt={tm.name} className="w-11 h-11 rounded-full object-cover" />
                  <div>
                    <div className="font-bold text-[#0A1C30] text-sm">{tm.name}</div>
                    <div className="text-xs text-[#6B7688]">{tm.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
        <div className="relative overflow-hidden rounded-[32px] bg-navy px-8 md:px-16 py-16 text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold text-white max-w-3xl mx-auto leading-tight">
            {t.ctaFinalPre} <span className="font-serif-it text-gold">{t.ctaFinalAccent}</span> {t.ctaFinalPost}
          </h2>
          <button
            onClick={() => setModal(true)}
            className="mt-8 inline-flex items-center gap-3 rounded-2xl bg-[var(--gold)] hover:bg-[var(--gold-soft)] text-[#0A1C30] font-extrabold text-lg px-10 py-4 transition-colors"
          >
            {t.ctaStart} <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-navy border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/logo-supera-light.png" alt="Supera Leadership" className="h-12 w-auto object-contain" />
          <p className="text-white/40 text-sm">© {new Date().getFullYear()} {t.footerRights}</p>
        </div>
      </footer>

      <RouteModal open={modal} onClose={() => setModal(false)} />
      <AssistantWidget />
    </div>
  );
}
